import type { TPropertyOption } from 'App/dashboards/_utils/useDashboardPropertyOptions'
import { useWaterPropertyOptions } from 'App/dashboards/_utils/useWaterPropertyOptions'
import moment from 'moment-timezone'
import { useCallback, useMemo, useRef } from 'react'
import { dashboardsStore } from 'stores/dashboardsStore/dashboardsStore'
import type { TProperty } from 'stores/fieldAssetStore/helperTypes'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'

// Water usage aggregates volume/duration/rain/ETc over the selected window, so (unlike the pest
// chart) there's no forecast tail — default to the last 2 weeks. Product-tweakable.
const DEFAULT_RANGE_DAYS = 14

const getDefaultDateRange = () => {
  const timezone = moment.tz.guess()

  return {
    from: moment.tz(timezone).subtract(DEFAULT_RANGE_DAYS, 'days').startOf('day'),
    to: moment.tz(timezone).endOf('day'),
  }
}

export type TWaterDashboardFilters = {
  // Every property the user has irrigation access to that has something the dashboard shows.
  options: TPropertyOption[]
  // Multiple selected properties (each renders as its own accordion section).
  selectedPropertyIds: string[]
  onPropertyChange: (values: string[]) => void
  dateFrom: moment.Moment
  dateTo: moment.Moment
  onDateRangeChange: (from: moment.Moment, to: moment.Moment) => void
}

/**
 * Per-dashboard multi-property selection + date range for the water family, persisted to the
 * dashboardsStore under the dashboard's stable id (in `propertyIds`, distinct from pest's single
 * `propertyId`). Default selection: saved ids (still valid) → the map's selected property (if valid)
 * → the first option. As with the pest hook, the auto-resolved fallback is NOT persisted — only an
 * explicit user change writes back.
 */
export const useWaterDashboardPreferences = ({
  dashboardId,
  hasAssets,
}: {
  dashboardId: string
  hasAssets: (property: TProperty) => boolean
}): TWaterDashboardFilters => {
  const options = useWaterPropertyOptions(hasAssets)
  const selectedPropertyFromMap = selectedFieldAssetsStore.useSelector((s) => s.property)
  const preference = dashboardsStore.useSelector((s) => s.preferencesByDashboardId[dashboardId])

  const selectedPropertyIds = useMemo(() => {
    const valid = new Set(options.map((o) => o.value))

    // Once the user has an explicit saved selection — including an empty one from "Clear all" —
    // respect it rather than snapping back to a default. `propertyIds` is undefined only when no
    // selection has ever been made for this dashboard.
    if (preference?.propertyIds !== undefined) {
      const saved = new Set(preference.propertyIds)

      // Option (alphabetical) order, not the order they were picked in.
      return options.filter((o) => saved.has(o.value)).map((o) => o.value)
    }

    // No saved selection yet → default to the map's property (if valid for water), else the first option.
    const mapId = selectedPropertyFromMap === null ? null : String(selectedPropertyFromMap)

    if (mapId && valid.has(mapId)) return [mapId]

    const first = options[0]?.value

    return first ? [first] : []
  }, [options, preference?.propertyIds, selectedPropertyFromMap])

  // Stable per-mount default so unchanged renders don't churn date-based fetch deps.
  const defaults = useRef(getDefaultDateRange())
  const timezone = moment.tz.guess()

  // Normalised to whole local days on read as well as on write. A range persisted before the picker
  // started normalising ends at 00:00 on its end date, which silently drops that day from anything
  // read at an hourly or finer interval. These are no-ops for a correctly stored range.
  const dateFrom = useMemo(
    () =>
      preference?.dateFrom ? moment.tz(preference.dateFrom, timezone).startOf('day') : defaults.current.from,
    [preference?.dateFrom, timezone],
  )

  const dateTo = useMemo(
    () => (preference?.dateTo ? moment.tz(preference.dateTo, timezone).endOf('day') : defaults.current.to),
    [preference?.dateTo, timezone],
  )

  // Every write persists the full preference, filling unchanged fields from the current effective
  // values — so changing only the date still captures the resolved properties, and vice-versa.
  const onPropertyChange = useCallback(
    (values: string[]) => {
      dashboardsStore.setState((s) => ({
        ...s,
        preferencesByDashboardId: {
          ...s.preferencesByDashboardId,
          [dashboardId]: {
            propertyId: null,
            propertyIds: values,
            dateFrom: dateFrom.toISOString(),
            dateTo: dateTo.toISOString(),
          },
        },
      }))
    },
    [dashboardId, dateFrom, dateTo],
  )

  const onDateRangeChange = useCallback(
    (from: moment.Moment, to: moment.Moment) => {
      dashboardsStore.setState((s) => ({
        ...s,
        preferencesByDashboardId: {
          ...s.preferencesByDashboardId,
          [dashboardId]: {
            propertyId: null,
            propertyIds: selectedPropertyIds,
            dateFrom: from.toISOString(),
            dateTo: to.toISOString(),
          },
        },
      }))
    },
    [dashboardId, selectedPropertyIds],
  )

  return useMemo(
    () => ({ options, selectedPropertyIds, onPropertyChange, dateFrom, dateTo, onDateRangeChange }),
    [options, selectedPropertyIds, onPropertyChange, dateFrom, dateTo, onDateRangeChange],
  )
}
